import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from 'Components/Alert';
import FieldSet from 'Components/FieldSet';
import FormGroup from 'Components/Form/FormGroup';
import FormLabel from 'Components/Form/FormLabel';
import SelectInput from 'Components/Form/SelectInput';
import TextArea from 'Components/Form/TextArea';
import TextInput from 'Components/Form/TextInput';
import Button from 'Components/Link/Button';
import Link from 'Components/Link/Link';
import ConfirmModal from 'Components/Modal/ConfirmModal';
import PageContent from 'Components/Page/PageContent';
import PageContentBody from 'Components/Page/PageContentBody';
import PageToolbar from 'Components/Page/Toolbar/PageToolbar';
import PageToolbarButton from 'Components/Page/Toolbar/PageToolbarButton';
import PageToolbarSection from 'Components/Page/Toolbar/PageToolbarSection';
import Table from 'Components/Table/Table';
import TableBody from 'Components/Table/TableBody';
import TableRow from 'Components/Table/TableRow';
import TableRowCell from '../components/TdarrTableRowCell';
import { align, icons, kinds } from 'Helpers/Props';
import FlowEditor from '../components/FlowEditor';
import { tdarrCrud, tdarrPost } from '../api';
import { useTdarrCollection, useTdarrLibraries } from '../queries';
import { TdarrFlowDocument, TdarrFlowPluginDefinition, TdarrFlowTemplate } from '../types';

interface ClassicPluginDefinition { id: string; source?: string; Operation?: string }
interface ClassicStackItem { id: string; source?: string; checked?: boolean; priority?: number; InputsDB?: Record<string, unknown> }

const templateColumns = [
  { name: 'name', label: 'Template', isVisible: true },
  { name: 'category', label: 'Category', isVisible: true },
  { name: 'plugins', label: 'Plugins', isVisible: true },
  { name: 'source', label: 'Source', isVisible: true },
  { name: 'actions', label: 'Actions', isVisible: true }
];
const stackColumns = [
  { name: 'library', label: 'Library', isVisible: true },
  { name: 'plugins', label: 'Enabled Plugins', isVisible: true },
  { name: 'actions', label: 'Actions', isVisible: true }
];

function normalizeFlow(item: Record<string, unknown>): TdarrFlowDocument {
  return {
    ...item,
    _id: String(item._id),
    name: String(item.name ?? item._id ?? 'Flow'),
    priority: Number(item.priority ?? 0),
    flowPlugins: Array.isArray(item.flowPlugins) ? item.flowPlugins : [],
    flowEdges: Array.isArray(item.flowEdges) ? item.flowEdges : [],
    isUiLocked: Boolean(item.isUiLocked)
  } as TdarrFlowDocument;
}

export default function Flows() {
  const history = useHistory();
  const { flowId = '' } = useParams<{ flowId?: string }>();
  const queryClient = useQueryClient();
  const query = useTdarrCollection('FlowsJSONDB');
  const libraries = useTdarrLibraries();
  const flows = useMemo(() => (query.data ?? []).map(normalizeFlow).sort((a, b) => a.priority - b.priority), [query.data]);
  const selected = flows.find((flow) => flow._id === flowId);
  const [importJson, setImportJson] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateSource, setTemplateSource] = useState('Community');
  const [deleteId, setDeleteId] = useState('');
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    if (query.isSuccess && flows.length && !selected) history.replace(`/flows/${flows[0]._id}`);
  }, [flows, history, query.isSuccess, selected]);

  const plugins = useQuery({
    queryKey: ['tdarr', 'flow-plugins'],
    queryFn: async () => {
      const [community, local] = await Promise.all([
        tdarrPost<[TdarrFlowPluginDefinition[], string]>('/search-flow-plugins', { data: { string: '', pluginType: 'Community' } }),
        tdarrPost<[TdarrFlowPluginDefinition[], string]>('/search-flow-plugins', { data: { string: '', pluginType: 'Local' } })
      ]);
      return [...(community[0] ?? []), ...(local[0] ?? [])];
    },
    staleTime: 5 * 60 * 1000
  });
  const templates = useQuery({
    queryKey: ['tdarr', 'flow-templates'],
    queryFn: async () => {
      const [community, local] = await Promise.all([
        tdarrPost<[TdarrFlowTemplate[], string]>('/search-flow-templates', { data: { string: '', pluginType: 'Community' } }),
        tdarrPost<[TdarrFlowTemplate[], string]>('/search-flow-templates', { data: { string: '', pluginType: 'Local' } })
      ]);
      return [...(community[0] ?? []), ...(local[0] ?? [])];
    },
    staleTime: 5 * 60 * 1000
  });
  const classicPlugins = useQuery({
    queryKey: ['tdarr', 'plugins', 'catalogue'],
    queryFn: async () => {
      const [community, local] = await Promise.all([
        tdarrPost<[ClassicPluginDefinition[], string]>('/search-plugins', { data: { string: '', pluginType: 'Community' } }),
        tdarrPost<[ClassicPluginDefinition[], string]>('/search-plugins', { data: { string: '', pluginType: 'Local' } })
      ]);
      return [...(community[0] ?? []), ...(local[0] ?? [])];
    },
    staleTime: 5 * 60 * 1000
  });
  const filteredTemplates = useMemo(() => {
    const needle = templateSearch.trim().toLowerCase();
    return (templates.data ?? []).filter((template) => template.sourceRepo === templateSource && (!needle || `${template.name} ${template.description ?? ''} ${template.categoryFolder ?? ''}`.toLowerCase().includes(needle)));
  }, [templateSearch, templateSource, templates.data]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['tdarr', 'collection', 'FlowsJSONDB'] });
  const create = useMutation({
    mutationFn: ({ id, source }: { id: string; source: Partial<TdarrFlowDocument> }) => {
      const flow: TdarrFlowDocument = { ...source, _id: id, name: String(source.name ?? `Flow ${flows.length + 1}`), priority: flows.length, flowPlugins: Array.isArray(source.flowPlugins) ? source.flowPlugins : [], flowEdges: Array.isArray(source.flowEdges) ? source.flowEdges : [], isUiLocked: Boolean(source.isUiLocked) };
      return tdarrCrud({ collection: 'FlowsJSONDB', mode: 'insert', docID: id, obj: flow });
    },
    onSuccess: async (_data, variables) => { await refresh(); setImportJson(''); setMessage({ kind: 'success', text: 'Flow created.' }); history.push(`/flows/${variables.id}`); },
    onError: () => setMessage({ kind: 'danger', text: 'Flow could not be created.' })
  });
  const save = useMutation({
    mutationFn: (flow: TdarrFlowDocument) => tdarrCrud({ collection: 'FlowsJSONDB', mode: 'update', docID: flow._id, obj: flow }),
    onSuccess: async () => { await refresh(); setMessage({ kind: 'success', text: 'Flow saved.' }); },
    onError: () => setMessage({ kind: 'danger', text: 'Flow could not be saved.' })
  });
  const remove = useMutation({
    mutationFn: (id: string) => tdarrCrud({ collection: 'FlowsJSONDB', mode: 'removeOne', docID: id, obj: {} }),
    onSuccess: async () => { setDeleteId(''); await refresh(); setMessage({ kind: 'success', text: 'Flow deleted.' }); history.push('/flows'); },
    onError: () => { setDeleteId(''); setMessage({ kind: 'danger', text: 'Flow could not be deleted.' }); }
  });
  const reorder = useMutation({
    mutationFn: async (direction: -1 | 1) => {
      if (!selected) return;
      const index = flows.findIndex((flow) => flow._id === selected._id); const target = flows[index + direction];
      if (!target) return;
      await Promise.all([
        tdarrCrud({ collection: 'FlowsJSONDB', mode: 'update', docID: selected._id, obj: { priority: target.priority } }),
        tdarrCrud({ collection: 'FlowsJSONDB', mode: 'update', docID: target._id, obj: { priority: selected.priority } })
      ]);
    },
    onSuccess: refresh,
    onError: () => setMessage({ kind: 'danger', text: 'Flow order could not be updated.' })
  });
  const isInUse = (id: string) => Boolean(libraries.data?.some((library) => library.flowId === id) || flows.some((flow) => flow._id !== id && flow.flowPlugins.some((plugin) => plugin.pluginName === 'goToFlow' && plugin.inputsDB?.flowId === id)));
  const selectedIndex = selected ? flows.findIndex((flow) => flow._id === selected._id) : -1;
  const createFrom = (source: Partial<TdarrFlowDocument>) => create.mutate({ id: crypto.randomUUID(), source });
  const importClassicStack = (libraryId: string) => {
    const library = libraries.data?.find((item) => item._id === libraryId);
    const stack = (library?.pluginIDs as ClassicStackItem[] | undefined)?.filter((item) => item.checked).sort((a, b) => Number(a.priority ?? 0) - Number(b.priority ?? 0)) ?? [];
    const findFlowPlugin = (name: string, version?: string) => (plugins.data ?? []).find((plugin) => plugin.sourceRepo === 'Community' && plugin.pluginName === name && (!version || plugin.version === version));
    const input = findFlowPlugin('inputFile'); const replacement = findFlowPlugin('replaceOriginalFile');
    const transcode = findFlowPlugin('runClassicTranscodePlugin', '2.0.0'); const filter = findFlowPlugin('runClassicFilterPlugin');
    if (!library || !input || !replacement || !transcode || !filter) { setMessage({ kind: 'danger', text: 'The required flow plugins are not available for classic-stack import.' }); return; }
    const makeNode = (definition: TdarrFlowPluginDefinition, y: number, inputsDB: Record<string, unknown> = {}, name = definition.name) => ({ id: crypto.randomUUID(), name, sourceRepo: definition.sourceRepo, pluginName: definition.pluginName, version: definition.version, position: { x: 420, y }, inputsDB, fpEnabled: true });
    const start = makeNode(input, 0); const end = makeNode(replacement, (stack.length + 1) * 150);
    const nodes = [start]; const edges = [];
    let previous = start;
    stack.forEach((item, index) => {
      const source = item.source ?? 'Community';
      const metadata = classicPlugins.data?.find((plugin) => plugin.id === item.id && (plugin.source ?? source) === source);
      const isFilter = metadata?.Operation === 'Filter'; const definition = isFilter ? filter : transcode;
      const node = makeNode(definition, (index + 1) * 150, { pluginSourceId: `${source}:${item.id}`, ...(item.InputsDB ?? {}) }, `${definition.name}: ${item.id}`);
      edges.push({ id: crypto.randomUUID(), source: previous.id, sourceHandle: '1', target: node.id, targetHandle: null });
      if (previous.pluginName === 'runClassicTranscodePlugin' && previous.version === '2.0.0') edges.push({ id: crypto.randomUUID(), source: previous.id, sourceHandle: '2', target: node.id, targetHandle: null });
      if (isFilter) edges.push({ id: crypto.randomUUID(), source: node.id, sourceHandle: '2', target: end.id, targetHandle: null });
      nodes.push(node); previous = node;
    });
    edges.push({ id: crypto.randomUUID(), source: previous.id, sourceHandle: '1', target: end.id, targetHandle: null });
    if (previous.pluginName === 'runClassicTranscodePlugin' && previous.version === '2.0.0') edges.push({ id: crypto.randomUUID(), source: previous.id, sourceHandle: '2', target: end.id, targetHandle: null });
    nodes.push(end);
    createFrom({ name: `${library.name} Flow`, flowPlugins: nodes, flowEdges: edges, isUiLocked: false });
  };
  const importFlow = () => {
    try { createFrom(JSON.parse(importJson) as Partial<TdarrFlowDocument>); }
    catch { setMessage({ kind: 'danger', text: 'Flow import failed. Check the JSON document.' }); }
  };
  const exportFlow = () => {
    if (!selected) return;
    const blob = new Blob([JSON.stringify(selected, null, 2)], { type: 'application/json' }); const url = URL.createObjectURL(blob); const link = document.createElement('a');
    link.href = url; link.download = `${selected.name}.json`; link.click(); URL.revokeObjectURL(url);
  };

  return <PageContent title="Flows">
    <PageToolbar><PageToolbarSection alignContent={align.LEFT}>
      <PageToolbarButton label="Add Blank Flow" iconName={icons.ADD} isSpinning={create.isPending} onPress={() => createFrom({})} />
      <PageToolbarButton label="Save Flow" iconName={icons.SAVE} isDisabled={!selected} isSpinning={save.isPending} onPress={() => document.querySelector<HTMLButtonElement>('#tdarr-save-flow')?.click()} />
      <PageToolbarButton label="Move Left" iconName={icons.ARROW_LEFT} isDisabled={selectedIndex <= 0 || reorder.isPending} onPress={() => reorder.mutate(-1)} />
      <PageToolbarButton label="Move Right" iconName={icons.ARROW_RIGHT} isDisabled={selectedIndex < 0 || selectedIndex >= flows.length - 1 || reorder.isPending} onPress={() => reorder.mutate(1)} />
      <PageToolbarButton label="Duplicate Flow" iconName={icons.CLONE} isDisabled={!selected || create.isPending} onPress={() => selected && createFrom({ ...selected, name: `${selected.name} (duplicate)` })} />
      <PageToolbarButton label="Export" iconName={icons.DOWNLOAD} isDisabled={!selected} onPress={exportFlow} />
    </PageToolbarSection></PageToolbar>
    <PageContentBody>
      {message && <Alert kind={message.kind}>{message.text}</Alert>}
      {query.isError && <Alert kind={kinds.DANGER}>Unable to load flows.</Alert>}
      {plugins.isError && <Alert kind={kinds.DANGER}>Unable to load the flow-plugin catalogue.</Alert>}
      {templates.isError && <Alert kind={kinds.DANGER}>Unable to load flow templates.</Alert>}
      {!!flows.length && <nav className="tdarr-tabs" aria-label="Flows">{flows.map((flow) => <Link key={flow._id} className={selected?._id === flow._id ? 'is-active' : undefined} to={`/flows/${flow._id}`}>{flow.name}</Link>)}</nav>}

      {!flows.length && query.isSuccess && <Alert>No flows are configured. Add a blank flow, use a template, or import JSON below.</Alert>}
      {selected && <FieldSet legend={selected.name}>
        <FlowEditor flow={selected} definitions={plugins.data ?? []} flows={flows} isSaving={save.isPending} onSave={(flow) => save.mutate(flow)} />
        <Button kind={kinds.DANGER} isDisabled={isInUse(selected._id)} onPress={() => setDeleteId(selected._id)}>Delete Flow</Button>
        {isInUse(selected._id) && <Alert kind={kinds.WARNING}>This flow is referenced by a library or another flow and cannot be deleted.</Alert>}
      </FieldSet>}

      <FieldSet legend="Add From Template or JSON">
        <FormGroup><FormLabel name="templateSource">Template Source</FormLabel><SelectInput<string> name="templateSource" value={templateSource} values={[{ key: 'Community', value: 'Community' }, { key: 'Local', value: 'Local' }]} onChange={({ value }) => setTemplateSource(value)} /></FormGroup>
        <FormGroup><FormLabel name="templateSearch">Templates</FormLabel><TextInput name="templateSearch" value={templateSearch} placeholder="Search templates" onChange={({ value }) => setTemplateSearch(String(value))} /></FormGroup>
        {!!filteredTemplates.length && <Table columns={templateColumns}><TableBody>{filteredTemplates.map((template) => <TableRow key={`${template.sourceRepo}-${template.templateName}`}><TableRowCell><strong>{template.name}</strong><br /><small>{template.description ?? template.templateName}</small></TableRowCell><TableRowCell>{template.categoryFolder ?? '—'}</TableRowCell><TableRowCell>{template.flowPlugins.length}</TableRowCell><TableRowCell>{template.sourceRepo}</TableRowCell><TableRowCell><Button size="small" kind={kinds.PRIMARY} isDisabled={create.isPending} onPress={() => createFrom({ ...template, name: template.name })}>Use Template</Button></TableRowCell></TableRow>)}</TableBody></Table>}
        {!!libraries.data?.some((library) => library.pluginIDs?.some((item) => item.checked)) && <><Alert>Convert an existing enabled classic-plugin stack into a visual flow.</Alert><Table columns={stackColumns}><TableBody>{libraries.data.filter((library) => library.pluginIDs?.some((item) => item.checked)).map((library) => <TableRow key={library._id}><TableRowCell>{library.name}</TableRowCell><TableRowCell>{library.pluginIDs?.filter((item) => item.checked).length ?? 0}</TableRowCell><TableRowCell><Button size="small" isDisabled={create.isPending || plugins.isLoading || classicPlugins.isLoading} onPress={() => importClassicStack(library._id)}>Import Stack</Button></TableRowCell></TableRow>)}</TableBody></Table></>}
        <FormGroup size="large"><FormLabel name="importFlow">Import JSON</FormLabel><TextArea name="importFlow" value={importJson} onChange={({ value }) => setImportJson(value)} /></FormGroup>
        <Button isDisabled={!importJson.trim() || create.isPending} onPress={importFlow}>Import as New Flow</Button>
      </FieldSet>
      <ConfirmModal isOpen={Boolean(deleteId)} kind="danger" title="Delete Flow" message={`Delete ${flows.find((flow) => flow._id === deleteId)?.name ?? deleteId}?`} confirmLabel="Delete" isSpinning={remove.isPending} onConfirm={() => remove.mutate(deleteId)} onCancel={() => setDeleteId('')} />
    </PageContentBody>
  </PageContent>;
}
